// Crop step for EVERY image upload in this app.
//
// Installed once (index.js -> installImageCrop()). It listens for "change" on any
// <input type="file"> in the capture phase - before React sees it - and, when the picked files
// include images, opens a small crop dialog for each image. The result (cropped, or the original
// when the person chooses "Use original") is put back on the same input and the change event is
// sent again, so the page's own onChange handler - or a ref read later on submit - gets the
// files exactly as before, just cropped. No upload screen had to change to get this.
//
//   - PDFs and other non-image files pass straight through untouched.
//   - Several photos at once: one dialog per photo ("Photo 2 of 6"), with "Use originals for the
//     rest" to skip ahead.
//   - Closing the dialog (X / Esc) keeps the original photo - it never cancels the upload.
//   - Opt out on an input with data-no-crop; preset a shape with data-crop-aspect="1" (or "4:3").
//   - Rotation is included, since phone photos often arrive sideways.
//
// Plain DOM, no React and no library, so it works the same in all three apps.

const STYLE_ID = "img-crop-style";
const SKIP = "__imgCropDone";
const ASPECTS = [
    { key: "free", label: "Free", value: null },
    { key: "1", label: "1:1", value: 1 },
    { key: "4:3", label: "4:3", value: 4 / 3 },
    { key: "3:4", label: "3:4", value: 3 / 4 },
    { key: "16:9", label: "16:9", value: 16 / 9 },
];
const MIN = 24;

const css = `
.imc-overlay{position:fixed;inset:0;z-index:20000;background:rgba(17,12,24,.72);display:flex;align-items:center;justify-content:center;padding:16px;font-family:inherit}
.imc-box{background:#fff;border-radius:16px;width:min(760px,100%);max-height:calc(100vh - 32px);display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.35);overflow:hidden}
.imc-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 18px;border-bottom:1px solid #eee}
.imc-head h2{margin:0;font-size:17px;font-weight:600;color:#1d1530}
.imc-head p{margin:2px 0 0;font-size:12.5px;color:#727c93}
.imc-x{border:0;background:none;width:36px;height:36px;border-radius:50%;font-size:22px;line-height:1;color:#727c93;cursor:pointer}
.imc-x:hover{background:#f4f0f4}
.imc-stage{position:relative;flex:1;min-height:200px;background:#1b1622;display:flex;align-items:center;justify-content:center;padding:14px;overflow:hidden;touch-action:none;user-select:none}
.imc-frame{position:relative;line-height:0}
.imc-frame img{display:block;max-width:100%;max-height:min(56vh,520px);-webkit-user-drag:none;pointer-events:none}
.imc-shade{position:absolute;inset:0;pointer-events:none}
.imc-rect{position:absolute;pointer-events:auto;box-shadow:0 0 0 9999px rgba(0,0,0,.55);outline:2px solid #fff;cursor:move;touch-action:none}
.imc-rect:before,.imc-rect:after{content:"";position:absolute;pointer-events:none;border:0 solid rgba(255,255,255,.45)}
.imc-rect:before{left:33.33%;right:33.33%;top:0;bottom:0;border-left-width:1px;border-right-width:1px}
.imc-rect:after{top:33.33%;bottom:33.33%;left:0;right:0;border-top-width:1px;border-bottom-width:1px}
.imc-h{position:absolute;width:18px;height:18px;background:#fff;border:2px solid #44084e;border-radius:4px;touch-action:none}
.imc-h[data-h=nw]{left:-9px;top:-9px;cursor:nwse-resize}.imc-h[data-h=ne]{right:-9px;top:-9px;cursor:nesw-resize}
.imc-h[data-h=sw]{left:-9px;bottom:-9px;cursor:nesw-resize}.imc-h[data-h=se]{right:-9px;bottom:-9px;cursor:nwse-resize}
.imc-h[data-h=n]{left:calc(50% - 9px);top:-9px;cursor:ns-resize}.imc-h[data-h=s]{left:calc(50% - 9px);bottom:-9px;cursor:ns-resize}
.imc-h[data-h=w]{top:calc(50% - 9px);left:-9px;cursor:ew-resize}.imc-h[data-h=e]{top:calc(50% - 9px);right:-9px;cursor:ew-resize}
.imc-tools{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:10px;padding:12px 18px;border-bottom:1px solid #eee}
.imc-chips{display:flex;flex-wrap:wrap;gap:6px}
.imc-chip{border:1px solid #e1dce8;background:#fff;color:#1d1530;border-radius:999px;padding:5px 12px;font-size:13px;cursor:pointer;font-family:inherit}
.imc-chip.on{background:#f4f0f4;border-color:#44084e;color:#44084e;font-weight:600}
.imc-foot{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:8px;padding:12px 18px;background:#fcfcfe}
.imc-btn{min-height:40px;padding:0 16px;border-radius:10px;border:1px solid #e1dce8;background:#fff;color:#44084e;font-size:14px;font-weight:500;cursor:pointer;font-family:inherit}
.imc-btn.primary{background:#44084e;border-color:#44084e;color:#fff}
.imc-btn.link{border-color:transparent;background:none;color:#727c93;margin-right:auto}
.imc-btn:disabled{opacity:.6;cursor:wait}
.imc-size{font-size:12px;color:#727c93}
@media (max-width:575px){.imc-overlay{padding:0;align-items:flex-end}.imc-box{border-radius:16px 16px 0 0;max-height:100vh}.imc-foot .imc-btn{flex:1}.imc-btn.link{flex-basis:100%;margin:0}}
`;

const ensureStyle = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
};

const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

const outputType = (file) => (file.type === "image/png" ? "image/png" : file.type === "image/webp" ? "image/webp" : "image/jpeg");
const extFor = (type) => ({ "image/png": "png", "image/webp": "webp" }[type] || "jpg");
const renamed = (name, type) => `${String(name || "image").replace(/\.[^.]+$/, "")}.${extFor(type)}`;

const canvasToFile = (canvas, file) => new Promise((resolve) => {
    let type = outputType(file);
    const done = (blob) => resolve(blob ? new File([blob], renamed(file.name, blob.type || type), { type: blob.type || type, lastModified: Date.now() }) : file);
    canvas.toBlob((blob) => {
        // Safari can't encode WebP - fall back to JPEG rather than failing.
        if (!blob && type === "image/webp") { type = "image/jpeg"; canvas.toBlob(done, type, 0.92); return; }
        done(blob);
    }, type, 0.92);
});

const parseAspect = (raw) => {
    if (!raw) return null;
    const match = String(raw).split(":").map(Number);
    if (match.length === 2 && match[0] > 0 && match[1] > 0) return match[0] / match[1];
    const n = Number(raw);
    return n > 0 ? n : null;
};

// One dialog for one image. Resolves to { file, rest } where rest === "originals" means
// "use the originals for the remaining photos".
const cropOne = (file, { index, total, aspect: initialAspect }) => new Promise((resolve) => {
    ensureStyle();
    const url = URL.createObjectURL(file);
    let rotation = 0; // degrees, multiples of 90
    let source = null; // the (rotated) image being shown
    let aspect = initialAspect;
    let rect = null; // in displayed px
    let busy = false;

    const overlay = document.createElement("div");
    overlay.className = "imc-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Crop photo");
    overlay.innerHTML = `
      <div class="imc-box">
        <div class="imc-head">
          <div><h2>Crop photo</h2><p>${total > 1 ? `Photo ${index + 1} of ${total} · ` : ""}Drag the box or its corners</p></div>
          <button type="button" class="imc-x" aria-label="Close and use the original">&times;</button>
        </div>
        <div class="imc-tools">
          <div class="imc-chips" role="group" aria-label="Shape">${ASPECTS.map((a) => `<button type="button" class="imc-chip" data-aspect="${a.key}">${a.label}</button>`).join("")}</div>
          <div class="imc-chips">
            <button type="button" class="imc-chip" data-rotate="-90" aria-label="Rotate left">⟲ Rotate</button>
            <button type="button" class="imc-chip" data-rotate="90" aria-label="Rotate right">⟳</button>
            <button type="button" class="imc-chip" data-reset="1">Reset</button>
          </div>
        </div>
        <div class="imc-stage"><div class="imc-frame"><img alt="Photo to crop"/><div class="imc-shade"><div class="imc-rect" tabindex="0" aria-label="Crop area">
          ${["nw", "n", "ne", "e", "se", "s", "sw", "w"].map((h) => `<span class="imc-h" data-h="${h}"></span>`).join("")}
        </div></div></div></div>
        <div class="imc-foot">
          ${total > 1 && index < total - 1 ? `<button type="button" class="imc-btn link" data-act="rest">Use originals for the rest</button>` : ""}
          <span class="imc-size" aria-live="polite"></span>
          <button type="button" class="imc-btn" data-act="original">Use original</button>
          <button type="button" class="imc-btn primary" data-act="crop">Crop &amp; use</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const img = overlay.querySelector(".imc-frame img");
    const rectEl = overlay.querySelector(".imc-rect");
    const sizeEl = overlay.querySelector(".imc-size");
    const cropBtn = overlay.querySelector('[data-act="crop"]');

    const bounds = () => ({ w: img.clientWidth, h: img.clientHeight });
    const paint = () => {
        if (!rect) return;
        rectEl.style.left = `${rect.x}px`;
        rectEl.style.top = `${rect.y}px`;
        rectEl.style.width = `${rect.w}px`;
        rectEl.style.height = `${rect.h}px`;
        const scale = source ? source.naturalWidth / img.clientWidth : 1;
        sizeEl.textContent = `${Math.round(rect.w * scale)} × ${Math.round(rect.h * scale)} px`;
    };
    const fullRect = () => {
        const { w, h } = bounds();
        if (!aspect) return { x: 0, y: 0, w, h };
        let cw = w; let ch = w / aspect;
        if (ch > h) { ch = h; cw = h * aspect; }
        return { x: (w - cw) / 2, y: (h - ch) / 2, w: cw, h: ch };
    };
    const markAspect = () => overlay.querySelectorAll("[data-aspect]").forEach((b) => {
        const a = ASPECTS.find((x) => x.key === b.dataset.aspect);
        b.classList.toggle("on", (a.value || null) === (aspect || null) || (a.value && aspect && Math.abs(a.value - aspect) < 0.001));
    });

    const render = async () => {
        const base = await loadImage(url);
        if (rotation % 360 === 0) {
            source = base;
        } else {
            const canvas = document.createElement("canvas");
            const quarter = Math.abs(rotation % 180) === 90;
            canvas.width = quarter ? base.naturalHeight : base.naturalWidth;
            canvas.height = quarter ? base.naturalWidth : base.naturalHeight;
            const ctx = canvas.getContext("2d");
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.drawImage(base, -base.naturalWidth / 2, -base.naturalHeight / 2);
            source = await loadImage(canvas.toDataURL("image/png"));
        }
        img.src = source.src;
        await new Promise((r) => (img.complete ? r() : (img.onload = r)));
        rect = fullRect();
        paint();
    };

    // ---- drag / resize ----
    let drag = null;
    const onDown = (event) => {
        if (!rect) return;
        event.preventDefault();
        const handle = event.target.dataset?.h || "move";
        drag = { handle, sx: event.clientX, sy: event.clientY, start: { ...rect } };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp, { once: true });
    };
    const onMove = (event) => {
        if (!drag) return;
        const { w: W, h: H } = bounds();
        const dx = event.clientX - drag.sx;
        const dy = event.clientY - drag.sy;
        const s = drag.start;
        let { x, y, w, h } = s;
        if (drag.handle === "move") {
            x = Math.min(Math.max(0, s.x + dx), W - s.w);
            y = Math.min(Math.max(0, s.y + dy), H - s.h);
        } else {
            const hd = drag.handle;
            if (hd.includes("e")) w = Math.min(Math.max(MIN, s.w + dx), W - s.x);
            if (hd.includes("s")) h = Math.min(Math.max(MIN, s.h + dy), H - s.y);
            if (hd.includes("w")) { const nx = Math.min(Math.max(0, s.x + dx), s.x + s.w - MIN); w = s.w + (s.x - nx); x = nx; }
            if (hd.includes("n")) { const ny = Math.min(Math.max(0, s.y + dy), s.y + s.h - MIN); h = s.h + (s.y - ny); y = ny; }
            if (aspect) {
                // Keep the shape: width drives height on corners/sides (height drives on n/s).
                if (hd === "n" || hd === "s") w = h * aspect; else h = w / aspect;
                if (hd.includes("n")) y = s.y + s.h - h;
                if (hd.includes("w")) x = s.x + s.w - w;
                if (hd === "n" || hd === "s") x = s.x + (s.w - w) / 2;
                if (hd === "e" || hd === "w") y = s.y + (s.h - h) / 2;
                // Stay inside the photo.
                if (x < 0 || y < 0 || x + w > W || y + h > H) return;
            }
        }
        rect = { x, y, w, h };
        paint();
    };
    const onUp = () => { drag = null; window.removeEventListener("pointermove", onMove); };
    rectEl.addEventListener("pointerdown", onDown);

    // Arrow keys nudge the box (keyboard access).
    rectEl.addEventListener("keydown", (event) => {
        if (!rect) return;
        const step = event.shiftKey ? 10 : 2;
        const { w: W, h: H } = bounds();
        const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[event.key];
        if (!moves) return;
        event.preventDefault();
        rect = { ...rect, x: Math.min(Math.max(0, rect.x + moves[0]), W - rect.w), y: Math.min(Math.max(0, rect.y + moves[1]), H - rect.h) };
        paint();
    });

    const onResize = () => { if (source) { rect = fullRect(); paint(); } };
    window.addEventListener("resize", onResize);

    const finish = (result) => {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("keydown", onKey, true);
        document.body.style.overflow = previousOverflow;
        overlay.remove();
        URL.revokeObjectURL(url);
        resolve(result);
    };
    const onKey = (event) => {
        if (event.key === "Escape") { event.stopPropagation(); finish({ file }); }
    };
    window.addEventListener("keydown", onKey, true);

    overlay.addEventListener("click", async (event) => {
        const target = event.target.closest("button");
        if (!target || busy) return;
        if (target.classList.contains("imc-x")) return finish({ file });
        if (target.dataset.aspect) {
            aspect = ASPECTS.find((a) => a.key === target.dataset.aspect)?.value || null;
            markAspect();
            rect = fullRect();
            return paint();
        }
        if (target.dataset.rotate) {
            rotation = (rotation + Number(target.dataset.rotate) + 360) % 360;
            return render();
        }
        if (target.dataset.reset) {
            rotation = 0; aspect = initialAspect; markAspect();
            return render();
        }
        if (target.dataset.act === "original") return finish({ file });
        if (target.dataset.act === "rest") return finish({ file, rest: "originals" });
        if (target.dataset.act === "crop") {
            busy = true;
            cropBtn.disabled = true;
            cropBtn.textContent = "Cropping...";
            const scale = source.naturalWidth / img.clientWidth;
            const NW = source.naturalWidth; const NH = source.naturalHeight;
            // Snap to the photo's edges when the box is within a pixel of them (screen rounding).
            const sx = rect.x * scale < scale ? 0 : Math.round(rect.x * scale);
            const sy = rect.y * scale < scale ? 0 : Math.round(rect.y * scale);
            const sw = NW - (sx + rect.w * scale) < scale ? NW - sx : Math.round(rect.w * scale);
            const sh = NH - (sy + rect.h * scale) < scale ? NH - sy : Math.round(rect.h * scale);
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, sw);
            canvas.height = Math.max(1, sh);
            const ctx = canvas.getContext("2d");
            if (outputType(file) === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
            ctx.drawImage(source, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            const out = await canvasToFile(canvas, file);
            return finish({ file: out });
        }
        return null;
    });

    markAspect();
    render().catch(() => finish({ file })); // unreadable image (e.g. HEIC on desktop) -> keep the original
    setTimeout(() => cropBtn.focus(), 50);
});

const isImage = (file) => /^image\//.test(file?.type || "") && !/svg|gif|heic|heif/i.test(file.type);

// Runs the crop dialog over a list of files; non-images (and SVG/GIF) are kept as they are.
export const cropImageFiles = async (fileList, options = {}) => {
    const files = Array.from(fileList || []);
    const images = files.filter(isImage);
    if (!images.length) return files;
    const out = [];
    let skipRest = false;
    let n = 0;
    for (const file of files) {
        if (!isImage(file) || skipRest) { out.push(file); continue; }
        // eslint-disable-next-line no-await-in-loop
        const result = await cropOne(file, { index: n, total: images.length, aspect: options.aspect || null });
        n += 1;
        out.push(result.file);
        if (result.rest === "originals") skipRest = true;
    }
    return out;
};

let installed = false;
export const installImageCrop = () => {
    if (installed || typeof window === "undefined" || typeof DataTransfer === "undefined") return;
    installed = true;
    window.addEventListener("change", (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== "file") return;
        if (event[SKIP] || input.hasAttribute("data-no-crop")) return;
        const files = Array.from(input.files || []);
        if (!files.some(isImage)) return;
        // Hold the event back from React until the photos are cropped.
        event.stopImmediatePropagation();
        event.stopPropagation();
        cropImageFiles(files, { aspect: parseAspect(input.getAttribute("data-crop-aspect")) }).then((result) => {
            try {
                const dt = new DataTransfer();
                result.forEach((f) => dt.items.add(f));
                input.files = dt.files;
            } catch (error) { /* very old browser - the originals stay on the input */ }
            const again = new Event("change", { bubbles: true });
            again[SKIP] = true;
            input.dispatchEvent(again);
        });
    }, true);
};

export default installImageCrop;
